import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ActionResultInteger } from '@portal/models/actionResultInteger';
import { ActionResultNegotiationStatisticsResponse } from '@portal/models/actionResultNegotiationStatisticsResponse';
import { ActionResultPageDisputePaginationShared } from '@portal/models/actionResultPageDisputePaginationShared';
import { ActionResultPageOrderModificationResponse } from '@portal/models/actionResultPageOrderModificationResponse';
import { ActionResultPagePaymentRefundResponse } from '@portal/models/actionResultPagePaymentRefundResponse';
import { ActionResultPagePromotionFullInfoShared } from '@portal/models/actionResultPagePromotionFullInfoShared';
import { ActionResultPageRequestQuotationShared } from '@portal/models/actionResultPageRequestQuotationShared';
import { ActionResultSellerReviewsPageResponse } from '@portal/models/actionResultSellerReviewsPageResponse';
import { DisputeShared } from '@portal/models/disputeShared';
import { OrderResponse } from '@portal/models/orderResponse';
import { PromotionFullInfoShared } from '@portal/models/promotionFullInfoShared';
import { RequestQuotationShared } from '@portal/models/requestQuotationShared';
import {
  disputesContext,
  flashSalesContext,
  makerCheckerClientContext,
  negotiationsContext,
  ordersCancellationsContext,
  ordersClientContext,
  requestsForQuotationsContext,
  returnsContext,
  reviewsContext,
} from '@portal/api-endpoints';
import { AuthService } from '@portal/auth';
import { PaginatorQuery } from '@portal/components';
import { DEFAULT_PAGE_NUM, ExtraSymbolsUrlEncoder, getHttpParams, replaceId } from '@portal/core';
import { UnreadMessagesService } from '../chats';
import { OrderStatusesService } from '../order-statuses/order-statuses.service';
import DisputeStatusEnum = DisputeShared.DisputeStatusEnum;
import StatusEnum = OrderResponse.StatusEnum;
import PromotionStatusEnum = PromotionFullInfoShared.PromotionStatusEnum;

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly unreadMessagesService = inject(UnreadMessagesService);
  private readonly orderStatusesService = inject(OrderStatusesService);

  loadChecksCount(): Observable<number> {
    return this.http.get<ActionResultInteger>(makerCheckerClientContext.checksCount).pipe(
      shareReplay(1),
      map(response => response.value),
    );
  }

  loadOrdersCount(): Observable<number> {
    const statusList = [StatusEnum.CREATED, StatusEnum.ASSEMBLY].flatMap(status =>
      this.orderStatusesService.getExtraStatusesIfDefined(status),
    );

    return this.http
      .get<ActionResultInteger>(ordersClientContext.countBadge, {
        params: getHttpParams({ statusList }),
      })
      .pipe(
        shareReplay(1),
        map(response => response.value),
      );
  }

  loadDisputesCount(): Observable<number> {
    return this.http
      .get<ActionResultPageDisputePaginationShared>(disputesContext.disputes, {
        params: getHttpParams({
          status: DisputeStatusEnum.NEW,
          'page.num': DEFAULT_PAGE_NUM,
          'page.size': DEFAULT_PAGE_NUM,
        } as PaginatorQuery),
      })
      .pipe(
        shareReplay(1),
        map((res: ActionResultPageDisputePaginationShared) => res.value.totalCount),
      );
  }

  loadRequestsForQuotationsCount(): Observable<number> {
    return this.http
      .get<ActionResultPageRequestQuotationShared>(requestsForQuotationsContext.all, {
        params: getHttpParams({
          statusList: [RequestQuotationShared.StatusEnum.ACTIVE, RequestQuotationShared.StatusEnum.AWAITING_SELLER],
          'page.num': DEFAULT_PAGE_NUM,
          'page.size': 1,
        } as PaginatorQuery),
      })
      .pipe(
        shareReplay(1),
        map((res: ActionResultPageDisputePaginationShared) => res.value.totalCount),
      );
  }

  loadNegotiationsCount(): Observable<number> {
    return this.http.get<ActionResultNegotiationStatisticsResponse>(negotiationsContext.statistics).pipe(
      shareReplay(1),
      map(r => r.value?.waitingSellerFeedback),
    );
  }

  loadNotCompletedRefunds(): Observable<number> {
    const params = getHttpParams({
      completed: false,
      'page.num': 1,
      'page.size': 1,
    });
    return this.http.get<ActionResultPagePaymentRefundResponse>(ordersCancellationsContext.all, { params }).pipe(
      shareReplay(1),
      map(response => response.value?.totalCount ?? 0),
    );
  }

  loadCreatedReturns(): Observable<number> {
    const params = getHttpParams({
      statuses: 'CREATED',
      'page.num': 1,
      'page.size': 1,
    });
    return this.http.get<ActionResultPagePaymentRefundResponse>(returnsContext.getReturns, { params }).pipe(
      shareReplay(1),
      map(response => response.value?.totalCount ?? 0),
    );
  }

  loadWaitingReviews(): Observable<number> {
    return this.authService.getUserInfo().pipe(
      switchMap(user =>
        this.http.get<ActionResultSellerReviewsPageResponse>(
          replaceId(reviewsContext.getReviewsBySeller, user?.sellerId || user?.id),
          {
            params: getHttpParams({
              'page.num': 1,
              'page.size': 1,
              includeScoreRating: true,
              excludeReviews: true,
            }),
          },
        ),
      ),
      map(res => res?.value?.summaryRating?.waitingSellerResponse),
    );
  }

  loadNotAppliedPromotions(): Observable<number> {
    return this.authService.getUserInfo().pipe(
      switchMap(user =>
        this.http.get<ActionResultPagePromotionFullInfoShared>(flashSalesContext.sellers, {
          params: getHttpParams({
            'page.num': 1,
            'page.size': 1,
            hasAppliedSkus: false,
            promotionStatus: PromotionStatusEnum.PREPARATION,
            sellerId: user?.sellerId || user?.sellerReferenceId,
          }),
        }),
      ),
      map(res => res?.value?.totalCount),
    );
  }

  loadUnreadMessagesCount(): Observable<number> {
    return this.unreadMessagesService.chatsSummary$.pipe(
      filter(Boolean),
      map(val => Object.values(val.chatType2totalUnreadMessages).reduce((acc, item) => acc + item, 0)),
    );
  }

  loadNotProcessedModifications(): Observable<number> {
    return this.http
      .get<ActionResultPageOrderModificationResponse>(ordersClientContext.cancelledOrdersAll, {
        params: getHttpParams({
          'page.num': 1,
          'page.size': 1,
          processed: false,
        }),
      })
      .pipe(map((res: ActionResultPageOrderModificationResponse) => res.value?.totalCount ?? 0));
  }
}
